'use client';

import { useRevealObserver } from '@/app/hooks/use-reveal-observer';
import { Nav, Hero, CommandCenter } from '@/app/components/landing/sections/top';
import {
  Alerts,
  VoiceHold,
  Donations,
  TtsEngine,
  HypeSquad,
  ChatArcade,
} from '@/app/components/landing/sections/modules';
import {
  Grow,
  MoreFeatures,
  FromStreamElements,
  Pricing,
  Faq,
  FinalCta,
} from '@/app/components/landing/sections/bottom';

/**
 * Landing-page orchestrator — warm-dark, suite-positioned.
 *
 * Holds the ONE global <style jsx> bundle and composes the sections from
 * @/app/components/landing/sections/*. The cockpit/chat/telemetry data is
 * local sample state in each section (illustrative animation, not wired to the
 * real app). Rendered by app/page.js, which owns the metadata + JSON-LD.
 */
export default function LandingClient({ viewerEnabled = false }) {
  useRevealObserver();

  return (
    <div className="dark bg-gum-gray-950 text-white min-h-screen antialiased">
      <style jsx global>{`
        html {
          scroll-behavior: smooth;
          scroll-padding-top: 80px;
        }

        /* ── Scroll reveal (driven by useRevealObserver → .visible) ───────── */
        .reveal {
          opacity: 0;
          transform: translateY(40px);
          transition:
            opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1),
            transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reveal.visible {
          opacity: 1;
          transform: translateY(0);
        }
        .reveal-left {
          opacity: 0;
          transform: translateX(-60px);
          transition:
            opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1),
            transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reveal-left.visible {
          opacity: 1;
          transform: translateX(0);
        }
        .reveal-right {
          opacity: 0;
          transform: translateX(60px);
          transition:
            opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1),
            transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reveal-right.visible {
          opacity: 1;
          transform: translateX(0);
        }
        .reveal-scale {
          opacity: 0;
          transform: scale(0.9);
          transition:
            opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1),
            transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reveal-scale.visible {
          opacity: 1;
          transform: scale(1);
        }
        .stagger > *:nth-child(1) {
          transition-delay: 0s;
        }
        .stagger > *:nth-child(2) {
          transition-delay: 0.1s;
        }
        .stagger > *:nth-child(3) {
          transition-delay: 0.2s;
        }
        .stagger > *:nth-child(4) {
          transition-delay: 0.3s;
        }
        .stagger > *:nth-child(5) {
          transition-delay: 0.4s;
        }
        .stagger > *:nth-child(6) {
          transition-delay: 0.5s;
        }

        /* ── Gradient text (hero accent + pink→purple→gold) ───────────────── */
        .gradient-text {
          background: linear-gradient(135deg, #ff90e8 0%, #9146ff 50%, #ffd700 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        /* ── Slot machine (hero rotating word) ────────────────────────────── */
        .slot {
          display: inline-block;
          position: relative;
          vertical-align: bottom;
          /* No overflow mask: it would clip g/j descenders (a bottom mask can't both hide the
             rising word and show its tails). The short rise below reads clean without one. */
          padding-bottom: 0.12em;
        }
        .slot-word {
          display: inline-block;
          will-change: transform, opacity, filter;
          animation: slotIn 0.85s cubic-bezier(0.16, 1, 0.3, 1);
          /* background-clip:text only paints the gradient INSIDE this box; the tight line-height
             clips the g/j tails. Pad the box down so the tails get painted; the negative margin
             cancels the layout shift so the subtitle stays where it was. */
          padding-bottom: 0.28em;
          margin-bottom: -0.28em;
        }
        @keyframes slotIn {
          0% {
            transform: translateY(0.5em) scale(0.96);
            opacity: 0;
            filter: blur(8px);
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translateY(0) scale(1);
            opacity: 1;
            filter: blur(0);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .slot-word {
            animation: none;
          }
        }

        /* ── Platform trio float ──────────────────────────────────────────── */
        @keyframes floatIcon {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-6px);
          }
        }
        .float-icon {
          animation: floatIcon 3s ease-in-out infinite;
        }
        .float-icon:nth-child(2) {
          animation-delay: 0.5s;
        }
        .float-icon:nth-child(3) {
          animation-delay: 1s;
        }

        /* micWave (audio waveform bars) lives in app/globals.css — it's applied
           via inline styles in the section components, which can't resolve a
           keyframe defined only inside this styled-jsx block. */

        /* ── Voice Hold — the streamer's talking performance ──────────────
           SVG parts animate around their own boxes (transform-box) so the
           face plays like a rig: irregular jabber (never a metronome), brow
           pops on offset delays, blinks in both states, head bob + pulsing
           speech arcs only while .vh-talk is on the root. */
        .vh-head,
        .vh-brow,
        .vh-eye,
        .vh-jaw,
        .vh-arc {
          transform-box: fill-box;
          transform-origin: center;
        }
        .vh-eye {
          animation: vhBlink 3.6s ease-in-out infinite;
        }
        .vh-brow {
          transition: transform 0.25s ease;
        }
        .vh-talk .vh-head {
          animation: vhBob 1.15s ease-in-out infinite;
        }
        .vh-talk .vh-jaw {
          animation: vhJabber 0.85s linear infinite;
        }
        .vh-talk .vh-brow-l {
          animation: vhBrow 1.7s ease-in-out infinite;
        }
        .vh-talk .vh-brow-r {
          animation: vhBrow 1.7s ease-in-out 0.35s infinite;
        }
        .vh-talk .vh-arc {
          animation: vhArc 0.85s ease-in-out infinite;
        }
        @keyframes vhBlink {
          0%,
          93%,
          100% {
            transform: scaleY(1);
          }
          95%,
          97% {
            transform: scaleY(0.12);
          }
        }
        @keyframes vhBob {
          0%,
          100% {
            transform: rotate(-1.4deg) translateY(0);
          }
          30% {
            transform: rotate(0.6deg) translateY(-0.5px);
          }
          55% {
            transform: rotate(1.3deg) translateY(0.3px);
          }
          80% {
            transform: rotate(-0.5deg) translateY(-0.4px);
          }
        }
        @keyframes vhJabber {
          0% {
            transform: scale(1, 0.25);
          }
          11% {
            transform: scale(0.92, 1);
          }
          23% {
            transform: scale(1.06, 0.45);
          }
          36% {
            transform: scale(0.95, 0.95);
          }
          49% {
            transform: scale(1.1, 0.18);
          }
          61% {
            transform: scale(0.9, 0.85);
          }
          72% {
            transform: scale(1.04, 0.4);
          }
          85% {
            transform: scale(0.95, 1);
          }
          100% {
            transform: scale(1, 0.25);
          }
        }
        @keyframes vhBrow {
          0%,
          40%,
          100% {
            transform: translateY(0) rotate(0);
          }
          14% {
            transform: translateY(-1.6px) rotate(-4deg);
          }
          26% {
            transform: translateY(-0.4px) rotate(1deg);
          }
          62% {
            transform: translateY(-0.9px) rotate(3deg);
          }
          76% {
            transform: translateY(0) rotate(0);
          }
        }
        @keyframes vhArc {
          0%,
          100% {
            transform: scaleX(0.9);
          }
          50% {
            transform: scaleX(1.08);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .vh-eye,
          .vh-talk .vh-head,
          .vh-talk .vh-jaw,
          .vh-talk .vh-brow-l,
          .vh-talk .vh-brow-r,
          .vh-talk .vh-arc {
            animation: none;
          }
        }

        /* ── TTS mode cards (stagger-in + cycling highlight) ──────────────── */
        .mode-card {
          opacity: 0;
          transform: translateY(30px);
          transition:
            opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1),
            transform 0.6s cubic-bezier(0.16, 1, 0.3, 1),
            border-color 0.4s ease,
            box-shadow 0.4s ease;
        }
        .mode-card.visible {
          opacity: 1;
          transform: translateY(0);
        }
        .mode-card.tts-highlight {
          border-color: #ff90e8 !important;
          box-shadow: 0 0 22px rgba(255, 144, 232, 0.28);
        }

        /* ── Alerts demo + arcade minis ───────────────────────────────────── */
        .alert-pop {
          animation: alertPop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .alert-slide-top {
          animation: alertSlideTop 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes alertPop {
          from {
            transform: scale(0.6) translateY(18px);
            opacity: 0;
          }
          to {
            transform: scale(1) translateY(0);
            opacity: 1;
          }
        }
        @keyframes alertSlideTop {
          from {
            transform: translateY(-46px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .arc-float {
          animation: arcFloat 1.4s ease-out forwards;
        }
        @keyframes arcFloat {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(-16px);
            opacity: 0;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .alert-pop,
          .alert-slide-top,
          .arc-float {
            animation: none;
          }
        }

        /* ── Donations — the 3-way tip strip (replicas of the real renders;
           see the TipTriptych comment in sections/modules.js for sources) ── */
        /* ① the alert card: the config's slideInRight at the real 600ms
           curve (card-keyframes.js) */
        .dono-alert-in {
          animation: donoAlertIn 600ms cubic-bezier(0.2, 0.85, 0.3, 1) both;
        }
        @keyframes donoAlertIn {
          from {
            opacity: 0;
            transform: translateX(40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        /* ② the Chat Box line — chat-templates/classic.js at fontSize 15
           (pad/radius/shadow ratios per its arithmetic) */
        .dono-chatrow {
          padding: 5px 7.5px;
          margin-bottom: 3px;
          border-radius: 4px;
          font-size: 15px;
          line-height: 1.4;
          text-align: left;
          word-break: break-word;
          overflow-wrap: anywhere;
          background-color: rgba(0, 0, 0, 0.5);
        }
        .dono-picon {
          display: inline-block;
          vertical-align: middle;
          margin-right: 4px;
          margin-bottom: 2px;
        }
        .dono-name {
          font-weight: 700;
          text-shadow: 1px 2px 3px rgba(0, 0, 0, 0.9);
          margin-right: 4px;
        }
        .dono-text {
          color: #fff;
          text-shadow: 1px 2px 3px rgba(0, 0, 0, 0.9);
        }
        /* the TIER-4 LEGENDARY row — classic.js tierStyles + globals.css
           bitsMega/tierShift4/rainbowGlow/impact-lines, replicated verbatim
           (fontSize ×1.15 = 17px at the demo's 15px base) */
        .dono-chatrow--tier4 {
          position: relative;
          z-index: 10;
          font-size: 17px;
          background-color: transparent;
          border: 3px solid transparent;
          background-image:
            linear-gradient(rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.9)),
            linear-gradient(120deg, #fde047, #fbbf24, #f97316, #c2410c, #f97316, #fbbf24, #fde047);
          background-size:
            100% 100%,
            300% 100%;
          background-origin: border-box;
          background-clip: padding-box, border-box;
          animation:
            donoBitsMega 0.8s cubic-bezier(0.18, 0.89, 0.32, 1.15),
            donoTierShift4 2s linear infinite;
        }
        /* the blurred legendary halo — globals.css .bits-tier4::before */
        .dono-chatrow--tier4::before {
          content: '';
          position: absolute;
          inset: -3px;
          border-radius: 8px;
          z-index: -1;
          pointer-events: none;
          background: linear-gradient(135deg, #fde047, #fbbf24, #f97316, #dc2626, #fbbf24, #fde047);
          background-size: 300% 300%;
          animation: donoRainbowGlow 2s linear infinite;
          opacity: 0.85;
          filter: blur(6px);
        }
        @keyframes donoBitsMega {
          0% {
            opacity: 0;
            transform: scale(2) rotate(-2deg);
            filter: brightness(3);
          }
          20% {
            opacity: 1;
            transform: scale(1.15) rotate(1deg);
            filter: brightness(1.5);
          }
          40% {
            transform: scale(0.95) rotate(-0.5deg);
            filter: brightness(1);
          }
          60% {
            transform: scale(1.05) rotate(0.3deg);
          }
          80% {
            transform: scale(0.98);
          }
          100% {
            transform: scale(1) rotate(0deg);
          }
        }
        @keyframes donoTierShift4 {
          0% {
            background-position:
              0% 50%,
              0% 50%;
          }
          100% {
            background-position:
              0% 50%,
              100% 50%;
          }
        }
        @keyframes donoRainbowGlow {
          0% {
            background-position: 0% 50%;
            opacity: 0.7;
          }
          50% {
            background-position: 100% 50%;
            opacity: 1;
          }
          100% {
            background-position: 0% 50%;
            opacity: 0.7;
          }
        }
        /* the amount badge — globals.css .bits-badge + -t4 (rainbow) */
        .dono-badge {
          display: inline-block;
          padding: 3px 10px;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 800;
          margin-right: 8px;
          vertical-align: middle;
          letter-spacing: 0.5px;
          background: linear-gradient(135deg, #fbbf24, #f97316, #fde047);
          background-size: 200% 200%;
          animation: donoRainbowGlow 2s linear infinite;
          color: #422006;
        }
        /* impact lines — globals.css .bits-impact-lines at the T4 gold */
        .dono-impact {
          position: absolute;
          inset: 0;
          overflow: hidden;
          border-radius: 4px;
          pointer-events: none;
        }
        .dono-impact::before,
        .dono-impact::after {
          content: '';
          position: absolute;
          height: 2px;
          width: 100%;
          background: linear-gradient(90deg, transparent, rgba(253, 224, 71, 0.85), transparent);
          animation: donoImpactLine 0.6s ease-out forwards;
          opacity: 0;
        }
        .dono-impact::before {
          top: 30%;
          animation-delay: 0.1s;
        }
        .dono-impact::after {
          top: 70%;
          animation-delay: 0.2s;
        }
        @keyframes donoImpactLine {
          0% {
            opacity: 0.8;
            transform: scaleX(0);
          }
          50% {
            opacity: 0.6;
            transform: scaleX(1);
          }
          100% {
            opacity: 0;
            transform: scaleX(1);
          }
        }
        /* the tier's screen shake — globals.css .shake-heavy, applied to the
           scene inside the mini stage (the frame is the OBS canvas edge) */
        .dono-shake {
          animation: donoShakeHeavy 0.8s ease-out;
        }
        @keyframes donoShakeHeavy {
          0%,
          100% {
            transform: translate(0) rotate(0);
          }
          5% {
            transform: translate(-10px, 6px) rotate(-0.8deg);
          }
          10% {
            transform: translate(10px, -7px) rotate(0.8deg);
          }
          15% {
            transform: translate(-9px, 8px) rotate(-0.6deg);
          }
          20% {
            transform: translate(9px, -6px) rotate(0.6deg);
          }
          25% {
            transform: translate(-7px, 5px) rotate(-0.5deg);
          }
          30% {
            transform: translate(7px, -4px) rotate(0.4deg);
          }
          40% {
            transform: translate(-5px, 3px) rotate(-0.2deg);
          }
          50% {
            transform: translate(5px, -2px) rotate(0.2deg);
          }
          65% {
            transform: translate(-3px, 2px);
          }
          80% {
            transform: translate(2px, -1px);
          }
        }
        /* the particle burst — lib/tier-colors.js T4 palette, riding the
           confetti keyframes' var contract from the row's center */
        .dono-spark {
          position: absolute;
          left: 50%;
          top: 50%;
          width: 6px;
          height: 6px;
          border-radius: 1px;
          pointer-events: none;
          opacity: 0;
          transform: translate(-50%, -50%);
          animation: donoConfetti 0.9s ease-out forwards;
        }
        /* ③ the Heckle hero moment — perimeter-figure.js replicas at the
           hs-bubble unit (--f ≈ 13px) */
        .dono-celebpop {
          animation: donoCelebPop 0.9s cubic-bezier(0.2, 1.5, 0.4, 1);
        }
        @keyframes donoCelebPop {
          0% {
            transform: scale(1);
          }
          40% {
            transform: scale(1.18) rotate(-3deg);
          }
          70% {
            transform: scale(0.97) rotate(1.5deg);
          }
          100% {
            transform: scale(1);
          }
        }
        .dono-celebadge {
          position: absolute;
          bottom: calc(100% + 5px);
          left: 50%;
          z-index: 3;
          padding: 2px 8px;
          border-radius: 999px;
          background: rgba(10, 10, 12, 0.9);
          font-size: 12px;
          font-weight: 900;
          letter-spacing: 0.04em;
          white-space: nowrap;
          text-shadow: 0 0 6px currentColor;
          animation: donoBadgeUp 1.8s ease-out both;
        }
        @keyframes donoBadgeUp {
          0% {
            opacity: 0;
            transform: translate(-50%, 6px) scale(0.4);
          }
          25% {
            opacity: 1;
            transform: translate(-50%, -3px) scale(1.05);
          }
          40% {
            transform: translate(-50%, -3px) scale(1);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -18px) scale(1);
          }
        }
        .dono-confetti {
          position: absolute;
          left: 50%;
          top: 40%;
          width: 6px;
          height: 6px;
          border-radius: 2px;
          pointer-events: none;
          opacity: 0;
          transform: translate(-50%, -50%);
          animation: donoConfetti 1.2s ease-out forwards;
        }
        @keyframes donoConfetti {
          0% {
            opacity: 1;
            transform: translate(-50%, -50%) rotate(0);
          }
          100% {
            opacity: 0;
            transform: translate(calc(-50% + var(--cdx)), calc(-50% + var(--cdy))) rotate(var(--crot));
          }
        }
        /* jersey nameplate — name alone on the back, 72% down the sprite */
        .dono-jersey {
          position: absolute;
          top: 72%;
          left: 50%;
          transform: translateX(-50%);
          max-width: 92%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.015em;
          color: rgba(255, 255, 255, 0.94);
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.75);
          pointer-events: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .dono-alert-in,
          .dono-chatrow--tier4,
          .dono-chatrow--tier4::before,
          .dono-badge,
          .dono-impact::before,
          .dono-impact::after,
          .dono-shake,
          .dono-celebpop,
          .dono-celebadge {
            animation: none;
          }
          .dono-confetti,
          .dono-spark {
            display: none;
          }
        }

        /* ── Hype Squad — heckle crowd demo (real cast art) ───────────────── */
        .hs-pop {
          transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .hs-pop-hidden {
          transform: translateY(115%);
        }
        .hs-turn {
          animation: hsTurn 0.32s ease-out;
          transform-origin: center;
        }
        @keyframes hsTurn {
          from {
            transform: scaleX(0.12);
          }
          to {
            transform: scaleX(1);
          }
        }
        .hs-fig {
          animation: hsBob 3.2s ease-in-out infinite;
          transform-origin: bottom center;
        }
        .hs-fig img {
          display: block;
        }
        @keyframes hsBob {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
        }
        /* Replica of the real overlay bubble (heckle-bubble.js pk-bubble at
           --f ≈ 13px): dark pill, accent-colored border (inline), platform
           glyph riding the first text line, white 600 text, centered tail,
           translateY rise-in. No name row — crowd bubbles are anonymous. */
        .hs-bubble {
          position: absolute;
          bottom: calc(100% + 10px);
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          align-items: flex-start;
          width: max-content;
          max-width: 190px;
          background: rgba(14, 14, 16, 0.86);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 11px;
          padding: 6px 11px;
          box-shadow: 0 5px 16px rgba(0, 0, 0, 0.45);
          backdrop-filter: blur(3px);
          color: #fff;
          font-size: 13px;
          font-weight: 600;
          line-height: 1.32;
          text-align: left;
          text-shadow: 1px 2px 2px rgba(0, 0, 0, 0.9);
          word-break: break-word;
          animation: hsPop 0.25s ease both;
          z-index: 20;
        }
        .hs-bubble::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid rgba(14, 14, 16, 0.86);
        }
        .hs-bplat {
          flex: none;
          display: inline-flex;
          align-items: center;
          margin-top: 2px;
          margin-right: 5px;
          filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.6));
        }
        @keyframes hsPop {
          from {
            transform: translate(-50%, 5px);
            opacity: 0;
          }
          to {
            transform: translate(-50%, 0);
            opacity: 1;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .hs-fig,
          .hs-bubble,
          .hs-turn {
            animation: none;
          }
          .hs-pop {
            transition: none;
          }
        }

        /* ── Sounds & Chat FX — mock OBS overlay + sound pills ────────────── */
        .mock-overlay {
          position: relative;
          max-width: 680px;
          margin: 0 auto;
          height: 340px;
          background: linear-gradient(180deg, rgba(10, 10, 14, 0.55), rgba(10, 10, 14, 0.92));
          border: 2px solid #2a2a31;
          border-radius: 14px;
          overflow: hidden;
          padding: 18px;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          gap: 5px;
        }
        .mock-overlay::before {
          content: 'OBS Preview';
          position: absolute;
          top: 10px;
          right: 14px;
          font-size: 10px;
          font-weight: 800;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 1.5px;
        }
        .mock-overlay::after {
          content: '';
          position: absolute;
          top: 13px;
          right: 78px;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #ef4444;
          box-shadow: 0 0 6px #ef4444;
          animation: recordPulse 1.5s ease-in-out infinite;
        }
        @keyframes recordPulse {
          0%,
          100% {
            opacity: 0.6;
          }
          50% {
            opacity: 1;
          }
        }
        .mock-msg {
          display: flex;
          align-items: baseline;
          gap: 8px;
          padding: 7px 12px;
          background: rgba(0, 0, 0, 0.5);
          border-radius: 6px;
          font-size: 16px;
          animation: mockMsgSlideIn 0.35s ease-out;
          word-break: break-word;
          flex-shrink: 0;
        }
        .mock-msg .name {
          font-weight: 700;
          flex-shrink: 0;
        }
        .mock-msg .text {
          color: #fff;
          text-align: left;
        }
        .mock-msg.sound-alert {
          background: rgba(255, 62, 142, 0.1);
          border-left: 3px solid #ff3e8e;
          color: #ff3e8e;
          font-weight: 600;
        }
        .mock-msg.sound-alert .icon {
          display: inline-block;
          animation: soundPulse 0.6s ease-out;
        }
        @keyframes mockMsgSlideIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes soundPulse {
          0% {
            transform: scale(0.6);
          }
          50% {
            transform: scale(1.3);
          }
          100% {
            transform: scale(1);
          }
        }
        /* Command center — live chat stream entrance */
        .cc-msg {
          animation: mockMsgSlideIn 0.35s ease-out;
        }
        /* Command center — activity feed drop-in (prepends at the top) */
        .cc-drop {
          animation: ccDrop 0.35s ease-out;
        }
        @keyframes ccDrop {
          from {
            opacity: 0;
            transform: translateY(-6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .cc-msg,
          .cc-drop,
          .mock-msg,
          .mock-msg.sound-alert .icon,
          [class*='vefx-'],
          [class*='vefx-'] > span {
            animation: none;
          }
        }
        .sound-row-label {
          font-size: 12px;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          font-weight: 700;
          margin-top: 28px;
          margin-bottom: 10px;
        }
        .sound-row {
          display: flex;
          gap: 8px;
          justify-content: center;
          flex-wrap: wrap;
        }
        .sound-pill {
          background: transparent;
          border: 2px solid #2a2a31;
          color: #f5f5f7;
          padding: 9px 16px;
          border-radius: 999px;
          cursor: pointer;
          font-family: ui-monospace, 'SF Mono', monospace;
          font-size: 13px;
          font-weight: 700;
          transition: all 0.15s;
          display: inline-flex;
          align-items: center;
          gap: 7px;
        }
        .sound-pill::before {
          content: '';
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #ff3e8e;
          display: inline-block;
          flex-shrink: 0;
        }
        .sound-pill:hover {
          border-color: #ff3e8e;
          background: rgba(255, 62, 142, 0.15);
          color: #ff3e8e;
        }
        .sound-pill.playing {
          border-color: #ff3e8e;
          background: #ff3e8e;
          color: #000;
        }
        .sound-pill.playing::before {
          background: #000;
          animation: pillPlayingPulse 0.5s ease-in-out infinite alternate;
        }
        @keyframes pillPlayingPulse {
          from {
            opacity: 1;
          }
          to {
            opacity: 0.3;
          }
        }

        /* Text-effect keyframes (carried; scoped vefx*) */
        .vefx-big {
          display: inline-block;
          animation: vefxBig 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) 1 forwards;
        }
        @keyframes vefxBig {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.5);
          }
        }
        .vefx-small {
          display: inline-block;
          animation: vefxSmall 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) 1 forwards;
        }
        @keyframes vefxSmall {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(0.6);
          }
        }
        .vefx-shake {
          display: inline-block;
          animation: vefxShake 0.9s ease-in-out 1 forwards;
        }
        @keyframes vefxShake {
          0%,
          100% {
            transform: translateX(0);
          }
          10% {
            transform: translateX(-5px) rotate(-3deg);
          }
          20% {
            transform: translateX(5px) rotate(3deg);
          }
          30% {
            transform: translateX(-4px) rotate(-2deg);
          }
          40% {
            transform: translateX(4px) rotate(2deg);
          }
          50% {
            transform: translateX(-3px) rotate(-1deg);
          }
          60% {
            transform: translateX(3px) rotate(1deg);
          }
          70%,
          100% {
            transform: translateX(0);
          }
        }
        .vefx-nod {
          display: inline-block;
          animation: vefxNod 1s ease-in-out 1 forwards;
        }
        @keyframes vefxNod {
          0%,
          100% {
            transform: translateY(0);
          }
          15% {
            transform: translateY(4px);
          }
          30% {
            transform: translateY(-4px);
          }
          45% {
            transform: translateY(4px);
          }
          60% {
            transform: translateY(-4px);
          }
          75%,
          100% {
            transform: translateY(0);
          }
        }
        .vefx-explode {
          display: inline-block;
          animation: vefxExplode 1.5s cubic-bezier(0.16, 1, 0.3, 1) 1 forwards;
        }
        @keyframes vefxExplode {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          30% {
            transform: scale(1.8);
            opacity: 1;
          }
          60% {
            transform: scale(2.6);
            opacity: 0;
          }
          61%,
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        .vefx-ripple > span {
          display: inline-block;
          animation: vefxRipple 1.4s ease-in-out 1 forwards;
          animation-delay: var(--char-delay, 0s);
        }
        @keyframes vefxRipple {
          0%,
          70%,
          100% {
            transform: translateY(0);
          }
          35% {
            transform: translateY(-10px);
          }
        }
        .vefx-bloom > span {
          display: inline-block;
          animation: vefxBloom 1.6s ease-in-out 1 forwards;
          animation-delay: var(--char-delay, 0s);
        }
        @keyframes vefxBloom {
          0%,
          80%,
          100% {
            transform: scale(1);
            color: inherit;
            font-weight: inherit;
          }
          40% {
            transform: scale(1.3);
            color: #ff3e8e;
            font-weight: 900;
          }
        }
        .vefx-jitter {
          display: inline-block;
          animation: vefxJitter 0.08s linear 8 forwards;
        }
        @keyframes vefxJitter {
          0%,
          100% {
            transform: translate(0, 0);
          }
          20% {
            transform: translate(-1px, 1px);
          }
          40% {
            transform: translate(1px, -1px);
          }
          60% {
            transform: translate(-1px, -1px);
          }
          80% {
            transform: translate(1px, 1px);
          }
        }
      `}</style>

      <Nav />
      <main>
        <Hero />
        <CommandCenter />
        <VoiceHold />
        <HypeSquad />
        <ChatArcade />
        <Alerts />
        <Donations />
        <Grow />
        <TtsEngine />
        <MoreFeatures />
        <FromStreamElements />
        <Pricing />
        <Faq />
      </main>
      <FinalCta viewerEnabled={viewerEnabled} />
    </div>
  );
}
